import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import FilterBar from "@/components/FilterBar";

const LEAVE_COLORS = {
  CL: "bg-blue-100 text-blue-700 border-blue-300",
  SL: "bg-rose-100 text-rose-700 border-rose-300",
  EL: "bg-emerald-100 text-emerald-700 border-emerald-300",
  ML: "bg-pink-100 text-pink-700 border-pink-300",
  PL: "bg-purple-100 text-purple-700 border-purple-300",
  LOP: "bg-amber-100 text-amber-700 border-amber-300",
  L: "bg-slate-200 text-slate-700 border-slate-300",
};

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}
function dateStr(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export default function TeamLeaveCalendar() {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/me/team-leaves/calendar", { params: { month, year } })
      .then((r) => setLeaves(r.data?.items || []))
      .catch(() => setLeaves([]))
      .finally(() => setLoading(false));
  }, [month, year]);

  const byEmployee = useMemo(() => {
    const grouped = new Map();
    leaves.forEach((l) => {
      if (!grouped.has(l.employee_id)) {
        grouped.set(l.employee_id, { name: l.employee_name, ranges: [] });
      }
      grouped.get(l.employee_id).ranges.push(l);
    });
    return Array.from(grouped.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [leaves]);

  const totalDays = daysInMonth(year, month);
  const days = Array.from({ length: totalDays }, (_, i) => i + 1);

  const dayCovered = (leaveItem, day) => {
    const d = dateStr(year, month, day);
    return d >= leaveItem.start_date && d <= leaveItem.end_date;
  };

  return (
    <AdminLayout title="Team Leave Calendar" breadcrumbs={["Home", "Self Service", "Team Leave"]}>
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          Approved leave for colleagues in your department — check who&apos;s out before you apply.
        </p>

        <FilterBar
          filters={[
            { type: "month", key: "month", label: "Month", value: month, onChange: setMonth },
            { type: "year", key: "year", label: "Year", value: year, onChange: setYear },
          ]}
        />

        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-x-auto" data-testid="team-leave-calendar">
          <table className="text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600 sticky left-0 bg-slate-50 min-w-[180px]">
                  Colleague
                </th>
                {days.map((d) => {
                  const dt = new Date(year, month - 1, d);
                  const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
                  const isToday =
                    d === today.getDate() &&
                    month === today.getMonth() + 1 &&
                    year === today.getFullYear();
                  return (
                    <th
                      key={d}
                      className={`px-1 py-2 text-center font-semibold min-w-[28px] ${
                        isToday ? "bg-sky-50 text-[#226DB4]" : isWeekend ? "bg-slate-100 text-slate-400" : "text-slate-600"
                      }`}
                    >
                      <div>{d}</div>
                      <div className="text-[9px] uppercase">{dt.toLocaleString("default", { weekday: "narrow" })}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={totalDays + 1} className="text-center py-10 text-slate-400">Loading…</td></tr>
              ) : byEmployee.length === 0 ? (
                <tr>
                  <td colSpan={totalDays + 1} className="text-center py-10 text-slate-400">
                    No approved leave in your department this month
                  </td>
                </tr>
              ) : byEmployee.map((emp) => (
                <tr key={emp.id}>
                  <td className="px-3 py-2 font-medium text-slate-700 sticky left-0 bg-white">{emp.name}</td>
                  {days.map((d) => {
                    const hit = emp.ranges.find((l) => dayCovered(l, d));
                    if (!hit) {
                      return <td key={d} className="px-1 py-1 text-center border-l border-slate-50 text-slate-200">·</td>;
                    }
                    const code = hit.leave_type_code || "L";
                    const tone = LEAVE_COLORS[code] || LEAVE_COLORS.L;
                    return (
                      <td key={d} className="px-1 py-1 text-center border-l border-slate-50">
                        <span
                          className={`inline-block w-5 h-5 rounded text-[10px] font-bold border ${tone}`}
                          title={`${hit.leave_type_name || code} · ${formatDisplayDate(hit.start_date)} – ${formatDisplayDate(hit.end_date)}`}
                        >
                          {String(code).charAt(0)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-slate-200 rounded p-3 flex flex-wrap gap-3 text-xs">
          {Object.entries(LEAVE_COLORS).filter(([k]) => k !== "L").map(([k, cls]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={`inline-block w-4 h-4 rounded border ${cls}`} />
              {k}
            </span>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
